import React, {useEffect, useState} from "react";
import {
    Checkbox,
    DefaultButton,
    DetailsList,
    FontIcon,
    IColumn,
    IContextualMenuItem,
    PanelType,
    PrimaryButton,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
    useTheme
} from "@fluentui/react";
import {toast} from "react-toastify";
import PanelWrapper from "../Common/PanelWrapper";
import {useGetFavoritesQuery, useGetLinkedItemTypesQuery, useLazyGetSearchResultsQuery, useSaveProductLinkedItemsMutation} from "../../store/Api";
import {IProduct, ISearchRequest} from "../../models/Search";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {NormalizeResult} from "../../logic/Product";
import {normalizeType, titleCaseType} from "./linkedItemHelpers";
import BestPriceDisplay from "../Products/BestPriceDisplay";

interface IAddLinkedItemPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    catalog: string;
    productId: string;
    existingLinkedIds: Set<string>;
}

const buildCriteria = (keywords: string, inStock: boolean): ISearchRequest => ({
    keywords,
    pageSize: 20,
    inStock,
    manufacturer: [],
    vendor: [],
    must: [],
    boost: [],
    exclude: [],
    filters: []
});

const AddLinkedItemPanel: React.FC<IAddLinkedItemPanelProps> = (props) => {
    const theme = useTheme();

    const [searchText, setSearchText] = useState<string>('');
    const [selectedProducts, setSelectedProducts] = useState<INormalizedProduct[]>([]);
    const [inStockOnly, setInStockOnly] = useState<boolean>(false);

    const [triggerSearch, searchResult] = useLazyGetSearchResultsQuery();
    const favorites = useGetFavoritesQuery();
    const typesQuery = useGetLinkedItemTypesQuery({catalog: props.catalog});
    const [saveLinks] = useSaveProductLinkedItemsMutation();

    // Selection lives in React state (not a Fluent Selection) so it survives new searches;
    // selected products stay pinned to the top of the list while the user keeps searching.
    const toggleSelected = (item: INormalizedProduct, checked?: boolean) => {
        setSelectedProducts(prev => {
            const has = prev.some(p => p.id === item.id);
            const want = checked ?? !has;
            if (want && !has) return [...prev, item];
            if (!want && has) return prev.filter(p => p.id !== item.id);
            return prev;
        });
    };

    // Debounced search-as-you-type against the current catalog.
    useEffect(() => {
        const term = searchText.trim();
        if (term.length < 2) return;
        const handle = setTimeout(() => {
            triggerSearch({catalog: props.catalog, criteria: buildCriteria(term, inStockOnly)});
        }, 300);
        return () => clearTimeout(handle);
    }, [searchText, inStockOnly, props.catalog, triggerSearch]);

    const selectedIds = new Set(selectedProducts.map(p => p.id));
    const searchItems: INormalizedProduct[] = (searchResult.data?.products ?? [])
        .filter(p => p.id !== props.productId)
        .map(p => NormalizeResult(p, favorites.data ? favorites.data[p.id] : undefined));
    // Selected products stay in place while they're part of the current results; once a new
    // search drops them out, they get pinned to the top so the selection stays visible.
    const searchIds = new Set(searchItems.map(p => p.id));
    const results: INormalizedProduct[] = [
        ...selectedProducts.filter(p => !searchIds.has(p.id)),
        ...searchItems
    ];

    // Defaults first, then any custom types the org has used (the endpoint orders them this way).
    const linkTypes: string[] = typesQuery.data?.length
        ? typesQuery.data
        : ['accessories', 'services', 'replacements', 'alternatives'];
    const defaultType = linkTypes[0];

    const columns: IColumn[] = [
        {
            key: 'select',
            name: '',
            minWidth: 30,
            maxWidth: 30,
            isIconOnly: true,
            onRender: (item: INormalizedProduct) => <Checkbox
                checked={selectedIds.has(item.id)}
                onChange={(_e, checked) => toggleSelected(item, checked)}
                ariaLabel={`Select ${item.name}`}
            />
        },
        {
            key: 'favorite',
            name: '',
            minWidth: 25,
            maxWidth: 25,
            isIconOnly: true,
            // Same colors/precedence as FavoritesDisplay: user favorite (gold) wins over company (green).
            onRender: (item: INormalizedProduct) => item.favorite?.user || item.favorite?.company
                ? <FontIcon
                    iconName="FavoriteStarFill"
                    title={item.favorite.user ? 'User favorite' : 'Company favorite'}
                    style={{verticalAlign: 'middle', fontSize: 16, color: item.favorite.user ? '#ffd700' : '#789c2f'}}
                />
                : <></>
        },
        {
            key: 'image',
            name: '',
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: INormalizedProduct) => item.image
                ? <img src={item.image} style={{maxHeight: '50px', maxWidth: '50px', margin: '0 auto'}} alt={item.name}/>
                : <></>
        },
        {
            key: 'name',
            name: 'Name',
            minWidth: 180,
            isMultiline: true,
            onRender: (item: IProduct) => <span>{item.name}</span>
        },
        {
            key: 'manufacturer',
            name: 'Manufacturer',
            minWidth: 110,
            maxWidth: 150,
            onRender: (item: IProduct) => <span>{item.manufacturer}</span>
        },
        {
            key: 'manufacturerPartNumber',
            name: 'Part Number',
            minWidth: 90,
            maxWidth: 120,
            onRender: (item: IProduct) => <span>{item.manufacturerPartNumber}</span>
        },
        {
            key: 'bestPrice',
            name: 'Best Price',
            minWidth: 150,
            maxWidth: 200,
            onRender: (item: INormalizedProduct) => <BestPriceDisplay product={item} />
        },
        {
            key: 'status',
            name: '',
            minWidth: 90,
            maxWidth: 90,
            onRender: (item: IProduct) => props.existingLinkedIds.has(item.id)
                ? <Text variant="small" styles={{root: {color: theme.palette.neutralSecondary}}}>Already linked</Text>
                : <></>
        }
    ];

    const handleAdd = async (linkType: string) => {
        if (selectedProducts.length === 0) return;
        const type = normalizeType(linkType);
        const count = selectedProducts.length;
        const toastId = toast.loading(count === 1 ? 'Saving linked item…' : `Saving ${count} linked items…`);
        try {
            await saveLinks({
                catalog: props.catalog,
                productId: props.productId,
                links: selectedProducts.map(p => ({linkedProductId: p.id, type, recommended: false}))
            }).unwrap();
            toast.update(toastId, {
                type: 'success',
                render: `Added ${count === 1 ? '1 item' : `${count} items`} to ${titleCaseType(type)}`,
                isLoading: false,
                autoClose: 3000
            });
            // Keep the panel open so several links can be added in a row.
            setSelectedProducts([]);
        } catch {
            toast.update(toastId, {type: 'error', render: 'Failed to save linked items', isLoading: false, autoClose: 4000});
        }
    };

    // Split button: the primary action adds to the default type; the menu offers "Add to {Type}"
    // for every known type. Links land un-recommended — that gets toggled on the pivot afterwards.
    const addMenuItems: IContextualMenuItem[] = linkTypes.map(t => ({
        key: t,
        text: `Add to ${titleCaseType(t)}`,
        onClick: () => { void handleAdd(t); }
    }));
    const footerButtons = [
        <Stack
            key="addLinkedItemFooter"
            horizontal
            verticalAlign="center"
            tokens={{childrenGap: '0.5em'}}
        >
            <PrimaryButton
                split
                text={`Add${selectedProducts.length > 0 ? ` ${selectedProducts.length}` : ''} to ${titleCaseType(defaultType)}`}
                iconProps={{iconName: 'Add'}}
                splitButtonAriaLabel="More link types"
                disabled={selectedProducts.length === 0}
                onClick={() => { void handleAdd(defaultType); }}
                menuProps={{items: addMenuItems}}
            />
            <DefaultButton text="Cancel" onClick={props.onDismiss}/>
            {selectedProducts.length > 0 &&
                <Text styles={{root: {color: theme.palette.neutralSecondary}}}>
                    {selectedProducts.length} selected
                </Text>}
        </Stack>
    ];

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title="Add Linked Item"
            type={PanelType.large}
            isFooterAtBottom
            footerButtons={footerButtons}
        >
            <Stack tokens={{childrenGap: '1em'}} styles={{root: {padding: '1em 0'}}}>
                <TextField
                    label="Search Products"
                    placeholder="Search by name, manufacturer, or part number"
                    value={searchText}
                    iconProps={{iconName: 'Search'}}
                    onChange={(_e, v) => setSearchText(v ?? '')}
                />
                <Checkbox
                    label="In Stock Only"
                    checked={inStockOnly}
                    onChange={(_e, checked) => setInStockOnly(checked ?? false)}
                />
                {searchResult.isFetching && <Spinner size={SpinnerSize.medium} label="Searching…"/>}
                {results.length > 0 &&
                    <DetailsList
                        compact
                        items={results}
                        columns={columns}
                        selectionMode={SelectionMode.none}
                        setKey="addLinkedItemResults"
                    />}
                {searchText.trim().length >= 2 && !searchResult.isFetching && results.length === 0 &&
                    <Text styles={{root: {color: theme.palette.neutralSecondary}}}>No products found.</Text>}
            </Stack>
        </PanelWrapper>
    );
};

export default AddLinkedItemPanel;
